import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Input, SalvButton, SalvDropdown, SalvModal } from '@salvapp/ui-components';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import {
  InternalApiEntitySchema,
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldType,
  InternalApiEntityType,
  UploadBatchDelimiterEnum,
  UploadBatchTypeEnum,
} from '../api-internal';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import BoxedExclamation from '../assets/img/icons/boxed-exclamation.svg';
import Papa from 'papaparse';
import './DataUploadPreview.scss';
import { isChildField, isComplexField } from '../schema/helper';
import { useQueryEntitySchema } from '../schema/entitySchemaActions';
import { useMe } from '../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';

dayjs.extend(customParseFormat);

const DATA_TYPES = [
  {
    label: 'Person',
    value: UploadBatchTypeEnum.PERSON,
    key: UploadBatchTypeEnum.PERSON,
  },
  {
    label: 'Person relation',
    value: UploadBatchTypeEnum.PERSON_RELATION,
    key: UploadBatchTypeEnum.PERSON_RELATION,
  },
  {
    label: 'Transaction',
    value: UploadBatchTypeEnum.TRANSACTION,
    key: UploadBatchTypeEnum.TRANSACTION,
  },
  {
    label: 'Transaction update',
    value: UploadBatchTypeEnum.TRANSACTION_UPDATE,
    key: UploadBatchTypeEnum.TRANSACTION_UPDATE,
  },
  {
    label: 'Custom list record',
    value: UploadBatchTypeEnum.CUSTOM_LIST_RECORD,
    key: UploadBatchTypeEnum.CUSTOM_LIST_RECORD,
  },
  {
    label: 'Custom list record relation',
    value: UploadBatchTypeEnum.CUSTOM_LIST_RECORD_RELATION,
    key: UploadBatchTypeEnum.CUSTOM_LIST_RECORD_RELATION,
  },
];

const ADMIN_DATA_TYPES = [
  {
    label: 'Person schema',
    value: UploadBatchTypeEnum.PERSON_SCHEMA,
    key: UploadBatchTypeEnum.PERSON_SCHEMA,
  },
  {
    label: 'Transaction schema',
    value: UploadBatchTypeEnum.TRANSACTION_SCHEMA,
    key: UploadBatchTypeEnum.TRANSACTION_SCHEMA,
  },
  {
    label: 'Scenario',
    value: UploadBatchTypeEnum.SCENARIO,
    key: UploadBatchTypeEnum.SCENARIO,
  },
  {
    label: 'Risk rule',
    value: UploadBatchTypeEnum.RISKRULE,
    key: UploadBatchTypeEnum.RISKRULE,
  },
  {
    label: 'Clearance rule',
    value: UploadBatchTypeEnum.CLEARANCE_RULE,
    key: UploadBatchTypeEnum.CLEARANCE_RULE,
  },
  {
    label: 'Query template',
    value: UploadBatchTypeEnum.QUERY_TEMPLATE,
    key: UploadBatchTypeEnum.QUERY_TEMPLATE,
  },
];

const ENTITY_DELETION_DATA_TYPES = [
  {
    label: 'Transaction deletion',
    value: UploadBatchTypeEnum.TRANSACTION_DELETE_LIST,
    key: UploadBatchTypeEnum.TRANSACTION_DELETE_LIST,
  },
  {
    label: 'Person deletion',
    value: UploadBatchTypeEnum.PERSON_DELETE_LIST,
    key: UploadBatchTypeEnum.PERSON_DELETE_LIST,
  },
];

const CUSTOM_LIST_RECORD_ARCHIVAL_DATA_TYPES = [
  {
    label: 'Custom list record archival',
    value: UploadBatchTypeEnum.CUSTOM_LIST_RECORD_ARCHIVE_LIST,
    key: UploadBatchTypeEnum.CUSTOM_LIST_RECORD_ARCHIVE_LIST,
  },
];

const PERSON_STATUS_DATA_TYPES = [
  {
    label: 'Person status',
    value: UploadBatchTypeEnum.PERSON_STATUS,
    key: UploadBatchTypeEnum.PERSON_STATUS,
  },
];

const getDataTypeOptions = (isGlobalAdmin: boolean, isCustomerAdmin: boolean, features: string[]) => {
  const options = [...DATA_TYPES];

  if (
    isGlobalAdmin ||
    (isCustomerAdmin && isFeatureEnabled(ORGANISATION_FEATURE.ENTITY_DELETION_VIA_CSV_FOR_NON_ADMIN, features))
  ) {
    options.push(...ENTITY_DELETION_DATA_TYPES);
    if (isFeatureEnabled(ORGANISATION_FEATURE.CUSTOM_LIST_RECORD_EXPORT_AND_ARCHIVE, features)) {
      options.push(...CUSTOM_LIST_RECORD_ARCHIVAL_DATA_TYPES);
    }
  }
  if (
    isGlobalAdmin ||
    (isCustomerAdmin && isFeatureEnabled(ORGANISATION_FEATURE.PERSON_STATUS_VIA_CSV_FOR_NON_ADMIN, features))
  ) {
    options.push(...PERSON_STATUS_DATA_TYPES);
  }
  if (isGlobalAdmin) {
    options.push(...ADMIN_DATA_TYPES);
  }

  return options;
};

const DELIMITER_MAPPINGS = {
  [UploadBatchDelimiterEnum.COMMA]: ',',
  [UploadBatchDelimiterEnum.TAB]: '\t',
  [UploadBatchDelimiterEnum.PIPE]: '|',
  [UploadBatchDelimiterEnum.SEMICOLON]: ';',
};

export const unstable_validateData = (value: string, field: InternalApiEntitySchemaField): boolean => {
  if (isComplexField(field)) {
    try {
      JSON.stringify(JSON.parse(value));
      return true;
    } catch {
      return false;
    }
  } else if (field.type === InternalApiEntitySchemaFieldType.DATETIME) {
    return dayjs(value).isValid();
  } else if (field.type === InternalApiEntitySchemaFieldType.NUMBER) {
    return value !== null && value !== '' && !isNaN(+value);
  }
  return true;
};

const hasEmptyValues = (data: string[]): boolean => data.some((value) => value.trim() === '');

interface CellError {
  rows: { [idx: number]: boolean };
  type: string;
  format?: string | undefined;
}

export const unstable_validateSchema = (schema: InternalApiEntitySchema, headers: string[], rows: FileRow[]) => {
  const errors: { [key: string]: CellError } = {};
  const lowerHeaders = headers.map((h) => h.toLowerCase());
  const fields = schema.schemaFields.filter(
    (field) =>
      ![
        InternalApiEntitySchemaFieldType.TEXT,
        InternalApiEntitySchemaFieldType.KEYWORD,
        InternalApiEntitySchemaFieldType.TEXT,
      ].includes(field.type) &&
      lowerHeaders.includes(field.fieldName?.toLowerCase()) &&
      !isChildField(field)
  );
  rows.forEach((row: FileRow, rowIdx: number) => {
    fields.forEach((field) => {
      const value = row[field.fieldName];
      if (!value) {
        return;
      }
      const isValid = unstable_validateData(value, field);
      if (isValid) {
        return;
      }
      if (!errors[field.fieldName]) {
        errors[field.fieldName] = {
          rows: { [rowIdx + 1]: true },
          type: field.type,
        };
      } else {
        errors[field.fieldName].rows[rowIdx + 1] = true;
      }
    });
  });
  return errors;
};

interface FileRow {
  [key: string]: string;
}

interface DataUploadPreviewProps {
  file: File | null;
  setShowModal: (value: boolean) => void;
  isUploading: boolean;
  upload: (type: UploadBatchTypeEnum | undefined, delimiter: UploadBatchDelimiterEnum) => void;
}

const DataUploadPreview = ({ file, setShowModal, isUploading, upload }: DataUploadPreviewProps) => {
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [fileRows, setFileRows] = useState<FileRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [invalidCells, setInvalidCells] = useState<{
    [name: string]: CellError;
  }>({});
  const [delimiter, setDelimiter] = useState<UploadBatchDelimiterEnum>(UploadBatchDelimiterEnum.COMMA);
  const [dataType, setDataType] = useState<DropdownOption | undefined>(undefined);
  const { isGlobalAdmin, isCustomerAdmin, features } = useMe();

  const dataTypeOptions = getDataTypeOptions(isGlobalAdmin, isCustomerAdmin, features);
  const { data: personSchema } = useQueryEntitySchema(InternalApiEntityType.PERSON);
  const { data: transactionSchema } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);

  useEffect(() => {
    if (file && delimiter) {
      Papa.parse<FileRow>(file, {
        delimiter: DELIMITER_MAPPINGS[delimiter],
        newline: '\n',
        quoteChar: '"',
        worker: false,
        header: true,
        complete: ({ data, meta: { fields } }) => {
          setFileHeaders(fields || []);
          setFileRows(data.splice(0, 10));
          if (fields !== undefined && hasEmptyValues(fields)) {
            setValidationErrors(['File header contains empty values']);
          }
        },
      });
    }
  }, [file, delimiter]);

  useEffect(() => {
    if (dataType?.value !== UploadBatchTypeEnum.TRANSACTION && dataType?.value !== UploadBatchTypeEnum.PERSON) {
      return;
    }

    const schema = dataType.value === UploadBatchTypeEnum.PERSON ? personSchema : transactionSchema;
    if (!schema) {
      return;
    }

    const errors = unstable_validateSchema(schema, fileHeaders, fileRows);

    // group errors
    const errorMessages = Object.keys(errors).map((fieldName) => {
      if (!errors[fieldName].format && errors[fieldName].type !== InternalApiEntitySchemaFieldType.DATETIME) {
        // format missing, so give a generic error
        return `Cannot correctly validate data for "${fieldName}" column due to missing schema format`;
      } else if (Object.keys(errors[fieldName].rows).length > 3) {
        // error is present on more than 3 rows, so give generic message
        return `Column "${fieldName}" values do not conform to schema format: ${
          errors[fieldName].format || errors[fieldName].type
        }`;
      } else {
        const rows = Object.keys(errors[fieldName].rows).join(', ');
        return `Column "${fieldName}" has invalid data on row${
          rows.length > 1 ? 's' : ''
        } ${rows} that do not conform to schema format: ${errors[fieldName].format || errors[fieldName].type}`;
      }
    });
    setInvalidCells(errors);
    setValidationErrors(errorMessages);
  }, [fileHeaders, fileRows, personSchema, transactionSchema, dataType]);

  return (
    <>
      <SalvModal.Body>
        <Row className='mb-4'>
          <Col md={12}>
            <SalvDropdown
              label='Data type'
              onChange={(opt: DropdownOption[]) => setDataType(opt[0])}
              value={dataType}
              name='type'
              options={dataTypeOptions}
              placeholder='Select...'
            />
          </Col>
        </Row>
        <Row className='mb-4'>
          <Col md={12}>
            <h3 className='preview-modal-h3'>Separate values using</h3>
            <div className='d-flex'>
              <Input.Radio
                size='small'
                label=','
                value={UploadBatchDelimiterEnum.COMMA}
                id='delimiterComma'
                name='delimiter'
                onChange={() => setDelimiter(UploadBatchDelimiterEnum.COMMA)}
                checked={delimiter === UploadBatchDelimiterEnum.COMMA}
                className='me-4'
                labelLocation='right'
              />
              <Input.Radio
                size='small'
                label=';'
                value={UploadBatchDelimiterEnum.SEMICOLON}
                id='delimiterSemicolon'
                name='delimiter'
                onChange={() => setDelimiter(UploadBatchDelimiterEnum.SEMICOLON)}
                checked={delimiter === UploadBatchDelimiterEnum.SEMICOLON}
                className='me-4'
                labelLocation='right'
              />
              <Input.Radio
                size='small'
                label='|'
                value={UploadBatchDelimiterEnum.PIPE}
                id='delimiterPipe'
                name='delimiter'
                onChange={() => setDelimiter(UploadBatchDelimiterEnum.PIPE)}
                checked={delimiter === UploadBatchDelimiterEnum.PIPE}
                className='me-4'
                labelLocation='right'
              />
              <Input.Radio
                size='small'
                label='Tab'
                value={UploadBatchDelimiterEnum.TAB}
                id='delimiterTab'
                name='delimiter'
                onChange={() => setDelimiter(UploadBatchDelimiterEnum.TAB)}
                checked={delimiter === UploadBatchDelimiterEnum.TAB}
                labelLocation='right'
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <label className='form-label'>Data preview</label>
            <table className='preview-table text-monospace'>
              <tbody>
                <tr>
                  <td className='preview-table'>1</td>
                  {fileHeaders.map((header: string) => (
                    <td className='preview-table' key={header}>
                      <span>{header}</span>
                    </td>
                  ))}
                </tr>
                {fileRows.map((row: FileRow, rowIndex: number) => (
                  <tr key={rowIndex}>
                    <td>{rowIndex + 2}</td>
                    {fileHeaders.map((header: string) => (
                      <td key={header} className={invalidCells[header]?.rows[rowIndex + 1] ? 'error' : ''}>
                        {row[header]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {validationErrors.length > 0 && (
              <>
                <div className='d-flex validation-error-title'>
                  <div className='d-flex align-items-baseline'>
                    <svg>
                      <BoxedExclamation />
                    </svg>
                  </div>
                  <div>
                    The following errors were identified during the initial file validation. Please fix the errors in
                    your CSV file and try uploading again. Note that we pre-validate only the first 10 rows.
                  </div>
                </div>
                <div className='validation-errors'>
                  {validationErrors.map((error: string, index: number) => (
                    <div className='error' key={index}>
                      {error}
                    </div>
                  ))}
                </div>
              </>
            )}
          </Col>
        </Row>
      </SalvModal.Body>
      <SalvModal.Footer>
        <SalvButton
          disabled={isUploading}
          onClick={() => {
            setShowModal(false);
          }}
          variant='muted'
          data-testid='cancel-button'
          label='Cancel'
        />
        <SalvButton
          disabled={isUploading || !dataType}
          onClick={() => upload(dataTypeOptions.find((opt) => opt.value === dataType?.value)?.value, delimiter)}
          variant='primary'
          data-testid='import-button'
          label='Upload'
        />
      </SalvModal.Footer>
    </>
  );
};

export default DataUploadPreview;
